import { useQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FacilityHit, PersonHit } from "@/components/AlgoliaSearch";
import {
  GLOBAL_SEARCH_FACILITIES_SEARCH_QUERY,
  GLOBAL_SEARCH_PEOPLE_SEARCH_QUERY,
  GLOBAL_SEARCH_RECENTLY_VIEWED_IDS_PER_TYPE
} from "@/components/GlobalSearch/queries";
import {
  navigableGlobalSearchHits,
  toGlobalSearchFacilityHit,
  toGlobalSearchPersonHit
} from "@/components/GlobalSearch/mapGlobalSearchHits";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { recentlyViewedEntityIds, useRecentlyVisited } from "@/hooks/useRecentlyVisited";

export const CREATE_INTERACTION_FUZZY_HITS_PER_PAGE = 15;
const DEBOUNCE_MS = 250;
const SCROLL_VISIBILITY_PADDING = 8;
const stickySearchHeaderSelector = "[data-algolia-sticky-search-header='true']";

function getScrollableAncestor(element: HTMLElement): HTMLElement | null {
  let current: HTMLElement | null = element.parentElement;

  while (current) {
    const { overflowY } = window.getComputedStyle(current);
    const canScroll = (overflowY === "auto" || overflowY === "scroll") && current.scrollHeight > current.clientHeight;

    if (canScroll) return current;

    current = current.parentElement;
  }

  return null;
}

function scrollHighlightedItemIntoView(highlightedItem: HTMLElement, fallbackContainer: HTMLElement) {
  const scrollContainer = getScrollableAncestor(highlightedItem) || fallbackContainer;
  const itemRect = highlightedItem.getBoundingClientRect();
  const containerRect = scrollContainer.getBoundingClientRect();
  const stickyHeader = scrollContainer.querySelector<HTMLElement>(stickySearchHeaderSelector);
  const stickyHeaderRect = stickyHeader?.getBoundingClientRect();
  const visibleTop =
    Math.max(containerRect.top, stickyHeaderRect?.bottom || containerRect.top) + SCROLL_VISIBILITY_PADDING;
  const visibleBottom = containerRect.bottom - SCROLL_VISIBILITY_PADDING;

  if (itemRect.top < visibleTop) {
    scrollContainer.scrollTo({
      top: scrollContainer.scrollTop - (visibleTop - itemRect.top)
    });
  } else if (itemRect.bottom > visibleBottom) {
    scrollContainer.scrollTo({
      top: scrollContainer.scrollTop + (itemRect.bottom - visibleBottom)
    });
  }
}

const EMPTY_PEOPLE: PersonHit[] = [];
const EMPTY_FACILITIES: FacilityHit[] = [];
const EMPTY_RECENTLY_VIEWED_IDS: string[] = [];

export type CreateInteractionFuzzyHit = PersonHit | FacilityHit;

export type UseCreateInteractionFuzzySearchReturn<T extends CreateInteractionFuzzyHit> = {
  query: string;
  displayValue: string | undefined;
  results: T[];
  isLoading: boolean;
  failed: boolean;
  highlightedIndex: number;
  setHighlightedIndex: React.Dispatch<React.SetStateAction<number>>;
  handleQueryChange: (query: string) => void;
  handleClear: () => void;
  handleItemSelected: (hit: T) => void;
  setIsFocused: (focused: boolean) => void;
  setDisplayValue: (value: string | undefined) => void;
  focusInput: () => void;
  itemRefs: React.MutableRefObject<Array<HTMLDivElement | null>>;
  resultsContainerRef: React.RefObject<HTMLDivElement>;
  searchBoxRef: React.RefObject<HTMLDivElement>;
};

type PersonOptions = {
  entity: "people";
  hitsPerPage?: number;
  skipListQuery?: boolean;
  getDisplayValue: (hit: PersonHit) => string;
  onSelect?: (hit: PersonHit) => void;
  onClear?: () => void;
};

type FacilityOptions = {
  entity: "facilities";
  hitsPerPage?: number;
  skipListQuery?: boolean;
  getDisplayValue: (hit: FacilityHit) => string;
  onSelect?: (hit: FacilityHit) => void;
  onClear?: () => void;
};

export function useCreateInteractionFuzzySearch(
  options: PersonOptions
): UseCreateInteractionFuzzySearchReturn<PersonHit>;
export function useCreateInteractionFuzzySearch(
  options: FacilityOptions
): UseCreateInteractionFuzzySearchReturn<FacilityHit>;
export function useCreateInteractionFuzzySearch(
  options: PersonOptions | FacilityOptions
): UseCreateInteractionFuzzySearchReturn<PersonHit> | UseCreateInteractionFuzzySearchReturn<FacilityHit> {
  const {
    entity,
    hitsPerPage = CREATE_INTERACTION_FUZZY_HITS_PER_PAGE,
    skipListQuery,
    getDisplayValue,
    onSelect,
    onClear
  } = options;
  const [currentQuery, setCurrentQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedValue, setSelectedValue] = useState<string | undefined>();
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const searchBoxRef = useRef<HTMLDivElement>(null!);
  const resultsContainerRef = useRef<HTMLDivElement>(null!);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const isFocusedRef = useRef(false);
  const highlightedIndexRef = useRef(highlightedIndex);
  highlightedIndexRef.current = highlightedIndex;
  const handleItemSelectedRef = useRef<(hit: CreateInteractionFuzzyHit) => void>(() => {});
  const onSelectRef = useRef<(hit: CreateInteractionFuzzyHit) => void>();
  onSelectRef.current = onSelect as ((hit: CreateInteractionFuzzyHit) => void) | undefined;
  const onClearRef = useRef(onClear);
  onClearRef.current = onClear;
  const getDisplayValueRef = useRef<(hit: CreateInteractionFuzzyHit) => string>(
    getDisplayValue as (hit: CreateInteractionFuzzyHit) => string
  );
  getDisplayValueRef.current = getDisplayValue as (hit: CreateInteractionFuzzyHit) => string;

  useEffect(() => {
    if (!currentQuery.trim()) {
      setDebouncedQuery("");
      return;
    }
    const timer = setTimeout(() => setDebouncedQuery(currentQuery), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [currentQuery]);

  const canViewPeople = useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPeople });
  const canViewFacilities = useHasCapabilityBool({
    domain: DOMAIN.FACILITIES,
    permission: PERMISSION.viewFacilities
  });
  const { getRecentlyVisitedItems } = useRecentlyVisited({ skipListQuery });
  const recentlyViewedIds = useMemo(() => {
    const items = getRecentlyVisitedItems({ capPerType: null });
    return recentlyViewedEntityIds(items, entity, GLOBAL_SEARCH_RECENTLY_VIEWED_IDS_PER_TYPE);
  }, [entity, getRecentlyVisitedItems]);

  const trimmed = debouncedQuery.trim();
  const skip = !trimmed;
  const skipPeople = skip || entity !== "people" || !canViewPeople;
  const skipFacilities = skip || entity !== "facilities" || !canViewFacilities;

  const peopleQuery = useQuery(GLOBAL_SEARCH_PEOPLE_SEARCH_QUERY, {
    variables: {
      search: trimmed,
      first: hitsPerPage,
      recentlyViewedIds: recentlyViewedIds.length > 0 ? recentlyViewedIds : EMPTY_RECENTLY_VIEWED_IDS
    },
    skip: skipPeople,
    fetchPolicy: "no-cache",
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true
  });
  const facilitiesQuery = useQuery(GLOBAL_SEARCH_FACILITIES_SEARCH_QUERY, {
    variables: {
      search: trimmed,
      first: hitsPerPage,
      recentlyViewedIds: recentlyViewedIds.length > 0 ? recentlyViewedIds : EMPTY_RECENTLY_VIEWED_IDS
    },
    skip: skipFacilities,
    fetchPolicy: "network-only",
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true
  });

  const liveTrimmed = currentQuery.trim();
  const networkInFlight = (!skipPeople && peopleQuery.loading) || (!skipFacilities && facilitiesQuery.loading);
  const peopleFailed = !skipPeople && Boolean(peopleQuery.error);
  const facilitiesFailed = !skipFacilities && Boolean(facilitiesQuery.error);
  const failed = entity === "people" ? peopleFailed : facilitiesFailed;

  const peopleHits = useMemo(() => {
    if (skipPeople || peopleFailed) return EMPTY_PEOPLE;
    const nodes = peopleQuery.data?.fuzzySearchPeople?.nodes;
    if (!nodes) return EMPTY_PEOPLE;
    return navigableGlobalSearchHits(nodes.map(toGlobalSearchPersonHit));
  }, [skipPeople, peopleFailed, peopleQuery.data]);
  const facilityHits = useMemo(() => {
    if (skipFacilities || facilitiesFailed) return EMPTY_FACILITIES;
    const nodes = facilitiesQuery.data?.fuzzySearchFacilities?.nodes;
    if (!nodes) return EMPTY_FACILITIES;
    return navigableGlobalSearchHits(nodes.map(toGlobalSearchFacilityHit));
  }, [skipFacilities, facilitiesFailed, facilitiesQuery.data]);
  const results: CreateInteractionFuzzyHit[] = entity === "people" ? peopleHits : facilityHits;
  const resultsRef = useRef(results);
  resultsRef.current = results;

  useEffect(() => {
    if (results.length === 1) {
      setHighlightedIndex(0);
    } else {
      setHighlightedIndex(-1);
    }
  }, [results]);

  useEffect(() => {
    if (!resultsContainerRef.current) return;
    if (highlightedIndex < 0) return;
    const highlightedItem = itemRefs.current[highlightedIndex];
    if (!highlightedItem) return;

    scrollHighlightedItemIntoView(highlightedItem, resultsContainerRef.current);
  }, [highlightedIndex]);

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      const textField =
        searchBoxRef.current?.querySelector<HTMLElement>("md-outlined-text-field") ||
        searchBoxRef.current?.querySelector<HTMLElement>("md-filled-text-field");
      textField?.focus();
    });
  }, []);

  const handleClear = useCallback(() => {
    setSelectedValue(undefined);
    setCurrentQuery("");
    setDebouncedQuery("");
    setHighlightedIndex(-1);
    onClearRef.current?.();
    focusInput();
  }, [focusInput]);

  const handleItemSelected = useCallback((hit: CreateInteractionFuzzyHit) => {
    setSelectedValue(getDisplayValueRef.current(hit));
    setCurrentQuery("");
    setDebouncedQuery("");
    setHighlightedIndex(-1);
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    onSelectRef.current?.(hit);
  }, []);
  handleItemSelectedRef.current = handleItemSelected;

  const handleQueryChange = useCallback((query: string) => {
    setCurrentQuery(query);
    if (query.trim() === "") {
      setDebouncedQuery("");
      setHighlightedIndex(-1);
    }
  }, []);

  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => {
      if (!isFocusedRef.current) return;

      const target = e.target as HTMLElement;
      if (target?.closest("[aria-label='Clear search query']")) return;

      const hits = resultsRef.current;
      const hasResults = hits.length > 0;
      const idx = highlightedIndexRef.current;

      if (e.key === "ArrowDown") {
        if (hasResults) {
          e.preventDefault();
          setHighlightedIndex((prev) => (prev + 1) % hits.length);
        }
        return;
      }

      if (e.key === "ArrowUp") {
        if (hasResults) {
          e.preventDefault();
          setHighlightedIndex((prev) => (prev - 1 + hits.length) % hits.length);
        }
        return;
      }

      if (e.key === "Enter") {
        if (idx >= 0 && idx < hits.length) {
          e.preventDefault();
          e.stopImmediatePropagation();
          handleItemSelectedRef.current(hits[idx]);
        }
      }
    };

    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, []);

  return {
    query: currentQuery,
    displayValue: selectedValue,
    results,
    isLoading: Boolean(liveTrimmed) && networkInFlight,
    failed,
    highlightedIndex,
    setHighlightedIndex,
    handleQueryChange,
    handleClear,
    handleItemSelected,
    setIsFocused: (focused: boolean) => {
      isFocusedRef.current = focused;
    },
    setDisplayValue: setSelectedValue,
    focusInput,
    itemRefs,
    resultsContainerRef,
    searchBoxRef
  } as UseCreateInteractionFuzzySearchReturn<PersonHit> | UseCreateInteractionFuzzySearchReturn<FacilityHit>;
}
